import { Home, Upload, Users, User, FolderLock, FileText, Bell, CreditCard, BarChart3, Download, UserPlus, Share2, Shield } from "lucide-react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { useCallback } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useProfileRoutePrefetch } from "@/hooks/useProfileRoutePrefetch";
import { useAuth } from "@/supabase-auth";
import { SidebarProfileFooter } from "@/components/sidebar/SidebarProfileFooter";
import oneFormIconDark from "@/assets/oneform-icon-dark.svg";
import oneFormIconLight from "@/assets/oneform-icon-light.svg";
import { PROFILE_ROUTES } from "@/routes/routes";

const navItems = [
  { title: "Dashboard", url: PROFILE_ROUTES.dashboard, icon: Home },
  { title: "Upload Documents", url: PROFILE_ROUTES.upload, icon: Upload },
  { title: "My Profile", url: PROFILE_ROUTES.settings, icon: User },
  { title: "Family Members", url: PROFILE_ROUTES.family, icon: Users },
  { title: "My Forms", url: PROFILE_ROUTES.forms, icon: FileText },
  { title: "Document Reminders", url: PROFILE_ROUTES.reminders, icon: Bell },
  { title: "Vault", url: PROFILE_ROUTES.vault, icon: FolderLock },
  { title: "Analytics", url: PROFILE_ROUTES.analytics, icon: BarChart3 },
  { title: "Billing", url: PROFILE_ROUTES.billing, icon: CreditCard },
  { title: "Data Export", url: PROFILE_ROUTES.dataExport, icon: Download },
  { title: "Delegates", url: PROFILE_ROUTES.delegates, icon: UserPlus },
  { title: "Share Forms", url: PROFILE_ROUTES.share, icon: Share2 },
  { title: "Consents", url: PROFILE_ROUTES.consents, icon: Shield },
];

export function UserSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const { prefetchRoute } = useProfileRoutePrefetch();
  const { session, profile } = useAuth();
  const userId = session?.user?.id;

  const isActive = (path: string) => location.pathname === path;

  // Prefetch route data on hover for faster navigation
  const handleMouseEnter = useCallback(
    (url: string) => {
      prefetchRoute(url, userId);
    },
    [prefetchRoute, userId]
  );

  // Prepare user data for profile footer
  const fullName = profile?.full_name || session?.user?.email?.split('@')[0] || 'User';
  const email = profile?.email || session?.user?.email || '';
  const avatarUrl = profile?.avatar_url;

  return (
    <Sidebar collapsible="icon" id="navigation" aria-label="Profile navigation" className="flex h-dvh flex-col">
      <SidebarHeader className="border-b">
        <Link to={PROFILE_ROUTES.dashboard} className="flex items-center gap-2 px-4 py-3">
          <img src={oneFormIconDark} alt="OneForm" className="w-6 h-6 dark:hidden flex-shrink-0" />
          <img src={oneFormIconLight} alt="OneForm" className="w-6 h-6 hidden dark:block flex-shrink-0" />
          {open && <span className="font-semibold">OneForm</span>}
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Profile</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink 
                      to={item.url} 
                      end
                      onMouseEnter={() => handleMouseEnter(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      {open && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      {/* Profile footer pinned to bottom */}
      <div className="mt-auto px-3 pb-3">
        <SidebarProfileFooter
          fullName={fullName}
          email={email}
          avatarUrl={avatarUrl}
        />
      </div>
    </Sidebar>
  );
}
